import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { PageFAQSchema, useCanonicalUrl } from '../components/seo/SchemaMarkup';

const FAQ_GROUPS = [
  {
    title: 'Orders & Shipping',
    faqs: [
      {
        question: 'How long does shipping take?',
        answer: 'Standard US orders arrive in 3-7 business days; expedited orders arrive in 2-3 business days. International orders typically take 7-14 business days. See our full Shipping page for cutoffs and rates.',
      },
      {
        question: 'Can I cancel or change my order after I place it?',
        answer: 'Orders can be modified or cancelled within 1 hour of placement. Email hello@ninjathlete.com from the address on the order with your order number — we ship fast, so we cannot guarantee changes after that window.',
      },
    ],
  },
  {
    title: 'Returns & Exchanges',
    faqs: [
      {
        question: 'What is your return policy?',
        answer: 'Apparel can be returned in new, unworn condition within 30 days of delivery. Digital products and items marked "Final Sale" are non-returnable. Full details on our Returns page.',
      },
      {
        question: 'How do I start a return or exchange?',
        answer: 'Visit ninjathlete.com/a/redo/returns, enter your order number and email, and follow the prompts. Customers who added Redo Coverage at checkout get free return shipping.',
      },
    ],
  },
  {
    title: 'Peptides & Supplements',
    faqs: [
      {
        question: 'Does NinjAthlete sell peptides directly?',
        answer: 'No. NinjAthlete publishes science-backed peptide education and protocols only. We partner with American Peptide Research (APR) for the highest-purity third-party-tested compounds — use code NINJA at /ref/16/ for 25% off.',
      },
      {
        question: 'Are your peptide protocols medical advice?',
        answer: 'No. All peptide content on NinjAthlete is educational and is not a substitute for medical advice. Always consult a licensed physician before starting any peptide, supplement, or training protocol.',
      },
    ],
  },
  {
    title: 'Membership & Rewards',
    faqs: [
      {
        question: 'How do I earn rewards points?',
        answer: 'You earn Dojo points for engaging with the platform — completing daily training check-ins, posting workouts to the feed, finishing learning paths, and referring friends. Points redeem at checkout for store discounts.',
      },
      {
        question: 'Do you offer a discount for first-time customers?',
        answer: 'Yes. Sign up for the NinjAthlete newsletter to instantly receive a 10% off welcome code, plus access to exclusive product drops and training plans.',
      },
    ],
  },
];

const ALL_FAQS = FAQ_GROUPS.flatMap(g => g.faqs);

export default function FAQPage() {
  useCanonicalUrl();
  return (
    <main className="pt-20 min-h-screen">
      <PageFAQSchema
        faqs={ALL_FAQS}
        pageName="NinjAthlete FAQ"
        pagePath="/faq"
        scriptId="site-faq-schema"
      />
      <div className="container-ninja section-spacing max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-amber-400" />
          </div>
          <h1
            className="text-4xl sm:text-5xl uppercase tracking-wider text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="faq-heading"
          >
            FAQ
          </h1>
          <p className="text-zinc-400 text-base max-w-lg mx-auto">
            The most common questions about orders, returns, peptides, and rewards — all in one place. Can&apos;t find what you&apos;re looking for?{' '}
            <Link to="/contact" className="text-white underline hover:text-amber-400 transition-colors">Contact us</Link>.
          </p>
        </div>

        {FAQ_GROUPS.map((group) => (
          <section key={group.title} className="mb-10" data-testid={`faq-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <h2
              className="text-xl uppercase tracking-wider text-white mb-4"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {group.title}
            </h2>
            <div className="space-y-2">
              {group.faqs.map((faq, i) => (
                <details
                  key={faq.question}
                  className="group border border-zinc-800 bg-zinc-900/40"
                  data-testid={`faq-item-${group.title.toLowerCase().replace(/\s+/g, '-')}-${i}`}
                >
                  <summary className="cursor-pointer px-5 py-4 text-sm text-zinc-200 font-medium flex items-center justify-between hover:bg-zinc-900/70 transition-colors">
                    <span>{faq.question}</span>
                    <span className="text-zinc-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <div className="px-5 pb-4 text-zinc-400 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}
